/* @jsx h */
import { useMenu } from "@app/vue/providers/Menu";
import { defineComponent, h, computed, ref } from "vue";
import { useRouter } from "vue-router";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { calculateCartSubtotal, formatPrice } from "@lemmonhq/orders";
import { cn } from "@lemmonhq/uikit";
import { useCustomer } from "../../providers";
import { createGroupOrder } from "@app/api/client";
import { getCharacterNameForCustomer } from "@app/vue/pages/ShopGroupOrder/utils/packs";

export type ShopRealtimeInfoProps = {
  onClose: () => void;
};

export const ShopRealtimeInfo = defineComponent<ShopRealtimeInfoProps>({
  name: "ShopRealtime",
  props: ["onClose"],
  setup(props) {
    const currency = computed(() => shop.value?.currency || "CHF");

    const router = useRouter();
    const { shop, realtime, groupOrder } = useMenu();
    const { t } = useLocalization();
    const { customer } = useCustomer();
    const isLeavingGroup = ref(false);
    const shareUrl = computed(
      () =>
        `${window.location.origin}/restaurant/${shop.value.slug}/group-order/${groupOrder.value?.id}`,
    );

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl.value);
        // Could add toast notification here
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    };

    const handleLeaveGroup = async () => {
      if (isLeavingGroup.value) return;

      try {
        isLeavingGroup.value = true;

        // Create a new group order
        const newGroupOrder = await createGroupOrder(
          shop.value.id,
          groupOrder.value?.table_id || undefined,
          "single",
        );

        // Redirect to the new group order
        router.push(
          `/restaurant/${shop.value.slug}/group-order/${newGroupOrder.id}`,
        );

        // Close the modal
        props.onClose();
      } catch (error) {
        console.error("Failed to leave group:", error);
        // Could add error notification here
      } finally {
        isLeavingGroup.value = false;
      }
    };

    console.log("ShopRealtimeInfo");

    return () => (
      <LemmonModal onClose={props.onClose} renderButtons={() => null}>
        <div class="space-y-6 p-6">
          {!realtime.enabled.value ? (
            // Form view when realtime is disabled
            <>
              <div class="text-center">
                <div class="text-3xl text-accent font-bold mb-2 flex justify-center items-center">
                  <span class="material-symbols-outlined mr-1 text-6xl">
                    {groupOrder.value?.table_id ? "qr_code_2" : "groups"}
                  </span>
                  {groupOrder.value?.table
                    ? t("customer.group_order.order_together_on", [
                        groupOrder.value?.table.name,
                      ])
                    : t("customer.group_order.order_together")}
                </div>
                <div class="text-lg text-base-content/70">
                  {t("customer.group_order.share_intro")}
                </div>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-semibold">
                    {t("customer.group_order.share_this_link")}
                  </span>
                </label>
                <div class="join">
                  <input
                    readonly
                    class="input input-bordered join-item flex-1 font-mono text-sm"
                    value={shareUrl.value}
                  />
                  <button
                    onClick={copyToClipboard}
                    class="btn btn-neutral join-item"
                    title={t("customer.group_order.copy_to_clipboard")}
                  >
                    <span class="material-symbols-outlined">content_copy</span>
                  </button>
                </div>
              </div>
              <div class="flex gap-2">
                <button
                  onClick={props.onClose}
                  class="btn btn-secondary  flex-1"
                >
                  {t("customer.group_order.go_back")}
                </button>
                <button
                  disabled={true}
                  onClick={() => {
                    //realtime.enable();
                    props.onClose();
                  }}
                  class="btn btn-primary  flex-1"
                >
                  {t("customer.group_order.start_group_order")}
                </button>
              </div>
            </>
          ) : (
            // Members view when realtime is enabled
            <>
              <div class="text-center">
                <div class="text-accent text-2xl font-bold mb-2 flex justify-center items-center">
                  <span class="material-symbols-outlined mr-1 text-6xl">
                    {groupOrder.value?.table_id ? "qr_code_2" : "groups"}
                  </span>
                  {groupOrder.value?.table
                    ? groupOrder.value?.table.name
                    : null}
                </div>
                <div class="text-sm text-base-content/70 mb-4">
                  {t("customer.group_order.invite_more_people")}
                </div>

                <div class="form-control mb-4">
                  <div class="join">
                    <input
                      readonly
                      class="input input-bordered input-sm join-item flex-1 font-mono text-xs text-accent"
                      value={shareUrl.value}
                    />
                    <button
                      onClick={copyToClipboard}
                      class="btn btn-neutral btn-sm join-item"
                    >
                      <span class="material-symbols-outlined text-sm">
                        content_copy
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="divider">
                {t("customer.group_order.group_members", [
                  realtime.groupState.value.members.length,
                ])}
              </div>

              <div class="space-y-3  overflow-y-auto">
                {realtime.groupState.value.members.length === 0 ? (
                  <div class="text-center py-8 text-base-content/50">
                    <span class="material-symbols-outlined text-4xl mb-2 block">
                      person_add
                    </span>
                    {t("customer.group_order.waiting_for_members")}
                  </div>
                ) : (
                  realtime.groupState.value.members.map((member, index) => {
                    // Get character name for this member
                    const characterName = groupOrder.value
                      ? getCharacterNameForCustomer(
                          groupOrder.value,
                          member.clientId,
                        )
                      : null;
                    const displayName =
                      characterName ||
                      member.clientName ||
                      t("customer.group_order.guest");

                    return (
                      <div
                        key={member.clientId}
                        class="card bg-base-200 shadow-sm border"
                      >
                        <div class="card-body p-4">
                          <div class="flex items-center justify-between">
                            <div class="flex items-center gap-3">
                              <div>
                                <div class="font-semibold text-accent">
                                  {displayName}
                                  {member.clientId === customer.value?.id ? (
                                    <span class="font-normal">
                                      {" "}
                                      ({t("customer.group_order.me")})
                                    </span>
                                  ) : null}
                                </div>
                                <div
                                  class={cn(
                                    "badge badge-sm mb-1 uppercase",
                                    member.cart.checkoutState === "confirmed"
                                      ? "badge-success"
                                      : "badge-warning",
                                  )}
                                >
                                  {member.cart.checkoutState
                                    ? t(
                                        `customer.group_order.checkout_state_${member.cart.checkoutState}`,
                                      )
                                    : t(
                                        "customer.group_order.checkout_state_ordering",
                                      )}
                                </div>
                              </div>
                            </div>

                            <div class="text-right">
                              <div class="text-sm font-semibold">
                                {t(
                                  member.cart.items.length === 1
                                    ? "customer.group_order.item_count_one"
                                    : "customer.group_order.item_count_many",
                                  [member.cart.items.length],
                                )}
                              </div>
                              <div class="text-xs text-base-content/60">
                                {formatPrice(
                                  calculateCartSubtotal(member.cart),
                                  true,
                                  2,
                                  currency.value,
                                )}
                              </div>
                            </div>
                          </div>

                          {member.cart.items.length > 0 && (
                            <div class="mt-2 pt-2 border-t border-base-300">
                              <div class="text-xs text-accent font-semibold mb-1">
                                {t("customer.group_order.products")}
                              </div>
                              <div class="flex flex-wrap gap-1">
                                {member.cart.items.map((item, itemIndex) => (
                                  <span
                                    key={itemIndex}
                                    class="badge badge-ghost badge-xs"
                                  >
                                    {item.quantity} x {item.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div class="flex gap-2">
                <button
                  disabled={isLeavingGroup.value}
                  onClick={handleLeaveGroup}
                  class="btn btn-secondary flex-1 hidden"
                >
                  {isLeavingGroup.value ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      {t("customer.group_order.leaving")}
                    </>
                  ) : (
                    t("customer.group_order.leave_group")
                  )}
                </button>
                <button onClick={props.onClose} class="btn btn-primary  flex-1">
                  {t("customer.group_order.continue_ordering")}
                </button>
              </div>
            </>
          )}
        </div>
      </LemmonModal>
    );
  },
});
